import React, { useContext } from 'react';
import { EnhancedEventCard } from '@eb/eds-event-card';

import { EventContext, UserContext } from '../../context';
import { Collection } from '../../types';

import {
    handleSaveEvent,
    handleShareOptionClick,
    handleShareClick,
    handleEventClick,
    handleShowAddToCollectionView,
    handleCreateCollection,
    handleAddOrRemoveToCollection,
} from './handlers';

import { EventCardEventData } from '../EventRenderer';

interface EventCardWithHandlersProps {
    style: string;
    type: string;
    imageStyle: string;
    containerSpacingClasses: string;
    isConsumer: boolean;
    ticketsBy: string;
    shouldConvertToUserTimezone: boolean;
    isAuthenticated?: boolean;
    shareOptions: object;
    hideSaveButton: boolean;
    userCollections?: Collection[];
}

export const SmartEventCard: React.FunctionComponent<
    EventCardEventData & EventCardWithHandlersProps
> = (props) => {
    const { dispatch, state } = useContext(EventContext);
    const user = useContext(UserContext);
    const isAuthenticated = !!user?.isAuthenticated;
    const userCollections = user?.collections?.userCollections || [];

    const _handleSaveEvent = async () => {
        // Optimistic state update
        const saveEventAction = (value: boolean) => ({
            type: 'save',
            payload: {
                id: props.id,
                value,
            },
        });

        isAuthenticated &&
            dispatch &&
            dispatch(saveEventAction(!props.savedByYou));

        const savedResponse = await handleSaveEvent({
            eventId: props.id,
            isAuthenticated,
            savedByYou: props.savedByYou,
        });

        if (savedResponse?.error) {
            // Handle error case
            dispatch && dispatch(saveEventAction(props.savedByYou));

            throw savedResponse.error;
        }
    };

    const _handleShowAddToCollectionView = async () => {
        const eventCollectionsFetched =
            state?.events[props.id].collectionsFetched;

        if (!eventCollectionsFetched) {
            const addToCollectionView = await handleShowAddToCollectionView({
                eventId: props.id,
                userCollections,
            });

            addToCollectionView &&
                dispatch &&
                dispatch({
                    type: 'update_event_collections',
                    payload: {
                        id: addToCollectionView?.id,
                        collections: addToCollectionView?.collections,
                    },
                });
        }
    };

    const _handleCreateCollection = async (collectionName: string) => {
        const currentEventCollections = state?.events[props.id].collections;
        const setUserCollections = user.collections?.setUserCollections;

        const createdCollection = await handleCreateCollection({
            eventId: props.id,
            name: collectionName,
        });

        if (createdCollection) {
            const userCollections = user.collections?.userCollections || [];
            setUserCollections &&
                setUserCollections([createdCollection, ...userCollections]);

            dispatch &&
                dispatch({
                    type: 'update_event_collections',
                    payload: {
                        id: props.id,
                        collections: [
                            ...currentEventCollections,
                            createdCollection.id,
                        ],
                    },
                });

            return createdCollection.id;
        }
    };

    const _handleAddOrRemoveToCollection = async ({
        isChecked,
        value,
    }: {
        isChecked: boolean;
        value: string;
    }) => {
        const addRemoveAction = (val: string[]) => ({
            type: 'update_event_collections',
            payload: {
                id: props.id,
                collections: val,
            },
        });

        const currentEventCollections =
            state?.events[props.id].collections || [];

        const updatedCollectionState = isChecked
            ? [...currentEventCollections, value]
            : currentEventCollections.filter((id) => id !== value);

        dispatch && dispatch(addRemoveAction(updatedCollectionState));

        const eventCollections = await handleAddOrRemoveToCollection({
            eventId: props.id,
            collectionId: value,
            isChecked,
        });

        if (eventCollections.error) {
            dispatch && dispatch(addRemoveAction(currentEventCollections));

            throw eventCollections.error;
        }
    };

    return (
        <EnhancedEventCard
            {...props}
            onClick={() => handleEventClick(props.id)}
            onSaveEvent={() => _handleSaveEvent()}
            onWillShareClick={() => handleShareClick(props.id)}
            onDidShareClick={(shareType: string) =>
                handleShareOptionClick(shareType)
            }
            onShowAddToCollectionView={() => _handleShowAddToCollectionView()}
            onCreateCollection={(name: string) => _handleCreateCollection(name)}
            onAddOrRemoveEventToCollection={(data: {
                isChecked: boolean;
                value: string;
            }) => _handleAddOrRemoveToCollection(data)}
        />
    );
};
